import { useEffect, useState, useMemo, useRef } from "react";
import { humanServerError, waitForCardRead } from "../lib/card-actions";
import { useParams, useNavigate, useLocation, type Location } from "react-router-dom";
import { useColumnSplit } from "../lib/useColumnSplit";
import { api, type Timesheet, type Employee } from "../lib/api";
import { bucketOf } from "../lib/confidence";
import { useViewport } from "../lib/useViewport";
import { PdfWithHighlights } from "../components/PdfWithHighlights";
import { AttentionCounter } from "../components/AttentionCounter";
import { ProgressTracker } from "../components/ProgressTracker";
import { DetailModal } from "../components/DetailModal";
import { classify, type Klasse } from "../lib/validator";
import {
  cellStatus,
  // (no-op marker for thresholds import below)
  STATUS_CLASS,
  needsAttention,
  statusBreakdown,
  type CellStatus,
} from "../lib/cellStatus";
import { formatTimeInput, isTimeField } from "../lib/timeFormat";
import { CardDetailActions } from "../components/CardDetailActions";
import { DetailViewShell } from "../components/DetailViewShell";
import { useThresholds } from "../lib/ClientSettingsContext";
import { cellPlausibility } from "../lib/plausibility";
import { cellAttention, attentionKey } from "../lib/cellAttention";
import { DayTable } from "../components/werkstatt/Phase4Berechnung";
import { ZulagenblattDetail } from "../components/ZulagenblattDetail";
import { timesheetViewFor } from "../lib/docTypeView";
import { zaKontoWieExport, zaWochenSaldo } from "../lib/zaKonto";

const FIELDS = ["von1", "bis1", "von2", "bis2", "symb"] as const;
type FieldName = typeof FIELDS[number];

interface DayData {
  von1?: string;
  bis1?: string;
  von2?: string;
  bis2?: string;
  symb?: string;
}

type ConfidenceMap = Partial<Record<FieldName, number>>;
type KlasseMap = Partial<Record<FieldName, Klasse>>;
type AuditMap = Partial<Record<FieldName, Record<string, unknown>>>;
type SuggestionMap = Record<string, Array<{ value: string; count: number }>>;

function getDayData(
  data: Timesheet["data"],
  day: number,
): {
  values:      DayData;
  ocrValues:   DayData;   // OCR-Original-Werte (ocr.value) für Diff-Anzeige in Compare-View
  confidences: ConfidenceMap;
  klassen:     KlasseMap;
  audits:      AuditMap;
} {
  // Per-Cell-Schema (Migration 034, 2026-05-19):
  //   data.days[day] = {
  //     day: N,
  //     von1: { ocr: {value,confidence,klasse,model,...}, value, confidence, klasse, corrections: [] },
  //     ...
  //   }
  // Effektiver Wert (vom User editierbar) liegt direkt unter .value;
  // der unveränderliche OCR-Snapshot unter .ocr.value.
  const daysMap = (data as any)?.days;
  const values:    DayData       = {};
  const ocrValues: DayData       = {};
  const confidences: ConfidenceMap = {};
  const klassen:     KlasseMap     = {};
  const audits:      AuditMap      = {};
  if (!daysMap || typeof daysMap !== "object") {
    return { values, ocrValues, confidences, klassen, audits };
  }
  const raw = daysMap[String(day)];
  if (!raw || typeof raw !== "object") {
    return { values, ocrValues, confidences, klassen, audits };
  }
  for (const f of FIELDS) {
    const cell = raw[f];
    if (cell && typeof cell === "object") {
      const ocr = cell.ocr && typeof cell.ocr === "object" ? cell.ocr : null;
      if (typeof cell.value === "string") values[f] = cell.value;
      else if (cell.value === null) values[f] = undefined;
      if (ocr && typeof ocr.value === "string") ocrValues[f] = ocr.value;
      // Konfidenz/Klasse: zuerst aus .ocr.* (OCR-Original-Konfidenz), Fallback
      // auf das ältere Top-Level-Feld der Zelle.
      const conf = ocr?.confidence ?? cell.confidence;
      if (typeof conf === "number") confidences[f] = conf;
      const kl = ocr?.klasse ?? cell.klasse;
      if (typeof kl === "string") klassen[f] = kl as Klasse;
      if (cell.audit && typeof cell.audit === "object") audits[f] = cell.audit as Record<string, unknown>;
    }
  }
  return { values, ocrValues, confidences, klassen, audits };
}

/** Reads totals from card.data.calculation. Supports both new (calc-abrechnung)
 * and legacy (calc-timesheet) shape via field-name aliasing. Returns "—" when
 * neither key exists. */
function readCalcTotal(data: unknown, newKey: string, legacyKey: string | null): number | string {
  const t = (data as any)?.calculation?.totals;
  if (!t) return "—";
  if (t[newKey] != null) return t[newKey];
  if (legacyKey && t[legacyKey] != null) return t[legacyKey];
  return "—";
}

function daysInMonth(month: string): number {
  const [y, m] = month.split("-").map(Number);
  return new Date(y!, m!, 0).getDate();
}

/** Click-to-copy chip für die volle Card-UUID. Zeigt die UUID monospaced
 *  und kopiert sie bei Klick in die Zwischenablage. Damit kann Thomas einfach
 *  card-IDs in Chat / Tickets referenzieren ohne die URL kopieren zu müssen. */
function CardIdChip({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — silent */ }
  };
  return (
    <>
      Karten-ID{" "}
      <code
        onClick={onCopy}
        title={copied ? "✓ kopiert" : `Klick zum Kopieren: ${id}`}
        style={{
          fontSize: 11, cursor: "pointer", userSelect: "all",
          padding: "1px 4px", borderRadius: 3,
          background: copied ? "#dcfce7" : "#f1f5f9",
          border: `1px solid ${copied ? "#86efac" : "#e2e8f0"}`,
        }}
      >
        {copied ? "✓ " : ""}{id}
      </code>
    </>
  );
}

export function TimesheetDetailPage({ modal }: { modal?: boolean } = {}) {
  const thresholds = useThresholds();
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const split = useColumnSplit("sibit:ts-split", 42);
  const location = useLocation();
  // When `modal` prop is set by App.tsx (which renders the second <Routes>
  // overlay), we are in modal-mode. The background location to return to is
  // in location.state.
  const background = (location.state as { background?: Location } | null)?.background;
  const [timesheet, setTimesheet] = useState<Timesheet | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [pendingEmployeeId, setPendingEmployeeId] = useState<string>("");
  const [dayData, setDayData] = useState<Record<number, DayData>>({});
  const [cellConfidence, setCellConfidence] = useState<Record<number, ConfidenceMap>>({});
  const [cellKlasse, setCellKlasse] = useState<Record<number, KlasseMap>>({});
  const [cellAudit, setCellAudit] = useState<Record<number, AuditMap>>({});
  const [cellOriginal, setCellOriginal] = useState<Record<number, DayData>>({});
  const [suggestions, setSuggestions] = useState<SuggestionMap>({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  // Bidirectional highlight: hovering a table cell highlights the matching
  // PDF region, and hovering a PDF region highlights the table cell.
  const [activeCell, setActiveCell] = useState<{ day: number; field: FieldName } | null>(null);
  // Bis die PDF-Vorschau geladen + die Markierungen ausgerichtet sind, sperrt
  // ein Vorhang den Detail-Body — davor sitzen die Rahmen kurz falsch und man
  // darf noch nichts eintragen. PdfWithHighlights meldet onReady. (Spiegel zur
  // Diäten-Detailseite — gleiche Grundfunktion auf beiden.)
  const [previewReady, setPreviewReady] = useState(false);
  useEffect(() => { setPreviewReady(false); }, [id]);
  // "OK so" acceptance per cell — when accepted, cell drops out of attention list.
  // In-memory only (resets on refresh; persistence can come later).
  const [acceptedCells, setAcceptedCells] = useState<ReadonlySet<string>>(new Set());
  const acceptCell = (day: number, field: FieldName) => {
    setAcceptedCells((prev) => {
      const n = new Set(prev);
      n.add(attentionKey(day, field));
      return n;
    });
  };
  const viewport = useViewport();
  // Compare-View toggle: "overview" = PDF + table side-by-side (default),
  // "compare" = per-cell PDF-crop next to input. Persists via localStorage.
  const [viewMode, setViewMode] = useState<"overview" | "compare">(() => {
    return (localStorage.getItem("sibit_ts_viewmode") as any) === "compare" ? "compare" : "overview";
  });
  const setViewModePersist = (m: "overview" | "compare") => {
    setViewMode(m);
    if (m === "compare") setCompareEverActivated(true);
    localStorage.setItem("sibit_ts_viewmode", m);
  };
  // Lazy-Mount für Compare-View (2026-05-19, Anlassfall: Karten-Open-Latenz
  // ~12s wegen 155+ Hintergrund-cell-image-Requests). Compare-View wird erst
  // ab dem ersten Klick auf "Vergleichen" gemountet — danach bleibt sie warm
  // (kein erneutes Mount beim Switch zurück zu overview). Initial-Mount der
  // overview ist instant. localStorage "viewmode=compare" → initial direkt
  // activated, sonst false.
  const [compareEverActivated, setCompareEverActivated] = useState<boolean>(
    () => localStorage.getItem("sibit_ts_viewmode") === "compare",
  );

  // Human Quality Gate (glassbox-pipeline-pattern.md §7b): Vergleichen nur
  // nach Freigabe der Markierungen in der Übersicht. Das Urteil wird mit
  // Kontext + History persistiert (POST /:id/highlight-review) — Lern-
  // Datensatz, analog zum Corrections-Tracking.
  const highlightReview = ((timesheet?.data as any)?.highlight_review ?? null) as
    | { status: "approved" | "blocked"; by_name?: string; at?: string }
    | null;
  const highlightApproved = highlightReview?.status === "approved";
  const [verdictBusy, setVerdictBusy] = useState(false);
  const [verdictErr, setVerdictErr] = useState<string | null>(null);
  // Mandatory Human Quality Gate (2026-06-22): vor dem Validieren muss die
  // Markierung beurteilt sein (stimmt/falsch) — Lern-Datensatz für die OCR.
  const [gateNotice, setGateNotice] = useState<string | null>(null);
  const gateRef = useRef<HTMLDivElement | null>(null);
  const sendHighlightVerdict = async (status: "approved" | "blocked") => {
    if (!timesheet) return;
    setVerdictBusy(true);
    setVerdictErr(null);
    try {
      const r = await fetch(
        new URL(`api/timesheets/${timesheet.id}/highlight-review`, document.baseURI).toString(),
        {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ status }),
        },
      );
      if (!r.ok) throw new Error(humanServerError(await r.text()));
      const body = await r.json();
      // Server-Echo prüfen: HTTP 200 reicht NICHT — Anlassfall 2026-06-09:
      // mergeCardData verwarf das Verdict still und der User bekam kein
      // Feedback. Gespeichert gilt erst, wenn das Verdict in der Antwort steht.
      if (body.timesheet?.data?.highlight_review?.status !== status) {
        throw new Error("Urteil wurde NICHT gespeichert (Server-Antwort ohne highlight_review)");
      }
      setTimesheet(body.timesheet);
      setGateNotice(null); // Markierung beurteilt → Validieren-Gate erfüllt
      if (status === "blocked") setViewModePersist("overview");
    } catch (e: any) {
      setVerdictErr(e?.message ?? String(e));
    } finally {
      setVerdictBusy(false);
    }
  };
  // Ohne Freigabe kein Vergleichen — auch nicht via localStorage-Restore.
  useEffect(() => {
    if (!timesheet) return;
    if (!highlightApproved && viewMode === "compare") setViewMode("overview");
  }, [timesheet, highlightApproved, viewMode]);

  useEffect(() => {
    if (!id) return;
    // Trace-Block für Debugging "Karte öffnen hängt" (2026-05-19). Loggt in
    // die Browser-Console: mount-trigger, fetch-Latenzen, total-time bis
    // ready-state. Aktiviert über localStorage 'sibit_trace_detail'='1' ODER
    // ?trace=1 in der URL. Default aktiv damit Thomas das Verhalten ohne
    // Setup live mitlesen kann (low overhead — paar console.log pro mount).
    const traceOn =
      localStorage.getItem("sibit_trace_detail") !== "0" &&
      true;
    const t0 = performance.now();
    const trace = (label: string, extra?: any) => {
      if (!traceOn) return;
      const dt = (performance.now() - t0).toFixed(0);
      const card = id?.slice(0, 8);
      // eslint-disable-next-line no-console
      console.log(`[detail ${card}] +${dt}ms ${label}`, extra ?? "");
    };
    trace("mount — fetching timesheet + employees");
    let cancelled = false;
    const ctrl = new AbortController();
    api.timesheets.get(id, ctrl.signal)
      .then((t) => {
        trace("timesheet.get done", { state: t.state, employee_id: t.employee_id });
        return api.employees.list(t.client_id, ctrl.signal).then((emps) => [t, emps] as const);
      })
      .then(([t, emps]) => {
        if (cancelled) { trace("ABORT after employees — effect cancelled"); return; }
        trace("employees.list done", { n_employees: emps.length });
        setTimesheet(t);
        setEmployees(emps);
        setPendingEmployeeId(t.employee_id ?? "");
        const initial: Record<number, DayData> = {};
        const origMap: Record<number, DayData> = {};
        const confMap: Record<number, ConfidenceMap> = {};
        const klasseMap: Record<number, KlasseMap> = {};
        const auditMap: Record<number, AuditMap> = {};
        for (let d = 1; d <= 31; d++) {
          const { values, ocrValues, confidences, klassen, audits } = getDayData(t.data, d);
          initial[d] = values;
          // cellOriginal = OCR-Snapshot (ocr.value), nicht der aktuelle Wert.
          // Damit zeigt der Compare-View den OCR-vs-User-Diff korrekt an,
          // auch wenn die Karte schon Korrekturen hat.
          origMap[d] = ocrValues;
          confMap[d] = confidences;
          klasseMap[d] = klassen;
          auditMap[d] = audits;
        }
        setDayData(initial);
        setCellOriginal(origMap);
        setCellConfidence(confMap);
        setCellKlasse(klasseMap);
        setCellAudit(auditMap);
        setDirty(false);
        trace("state populated — DetailPage ready");
      })
      .catch((e) => {
        if (cancelled) return;
        trace("ERROR", { name: e?.name, message: e?.message, status: e?.status });
        setErr(e.message);
      });
    return () => {
      cancelled = true;
      ctrl.abort();
      trace("unmount (effect cleanup)");
    };
  }, [id]);

  // Datalist-Suggestions sind seit Migration 034 leer — der Closed-Loop
  // muss auf corrections[]-Aggregation umgestellt werden (eigener PR).
  useEffect(() => {
    setSuggestions({});
  }, [pendingEmployeeId]);

  const days = useMemo(() => timesheet ? daysInMonth(timesheet.month) : 0, [timesheet]);

  const setField = (day: number, field: FieldName, value: string) => {
    // Defensive: ignore mutations when card is read-only. The UI disables
    // inputs, but paste/keyboard paths might still fire. Server would 409.
    const st = timesheet?.state;
    if (st && (st === "calculated" || st === "exported" || st === "archived" || st === "superseded")) {
      return;
    }
    setDayData((prev) => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
    setDirty(true);
  };

  // Per-cell attention combines confidence-status + plausibility + acceptance.
  // Both Übersicht + Vergleichen views use this single source of truth.
  //
  // Post-Validate-Auto-Accept (Owner-Spec 2026-05-18): sobald die Karte den
  // state >= validated erreicht hat, gilt der User-Klick auf „Validieren" als
  // pauschale Akzeptanz aller noch offenen Attention-Items. Das schließt
  // confidence-low + plausibility-red/yellow ein. Die Items werden also nicht
  // gefiltert sondern als „accepted" markiert — Breakdown bleibt sichtbar
  // (Audit-Info: was war einmal auffällig), aber attentionItems geht auf 0
  // und der AttentionCounter wird leer.
  type AttentionItem = { day: number; field: FieldName };
  const postValidate = !!timesheet && ["validated", "calculated", "exported", "archived", "superseded"]
    .includes(timesheet.state);
  const { breakdown, attentionItems, plausCounts } = useMemo(() => {
    const items: AttentionItem[] = [];
    const statuses: CellStatus[] = [];
    let nPlausRed = 0, nPlausYellow = 0;
    for (let d = 1; d <= days; d++) {
      const values = dayData[d] ?? {};
      const conf = cellConfidence[d] ?? {};
      const klassen = cellKlasse[d] ?? {};
      const orig = cellOriginal[d] ?? {};
      for (const f of FIELDS) {
        const att = cellAttention(
          { field: f, value: values[f], origValue: orig[f], confidence: conf[f], klasse: klassen[f], day: values as any },
          thresholds, acceptedCells, attentionKey(d, f),
        );
        statuses.push(att.status);
        if (att.plaus === "red") nPlausRed++;
        else if (att.plaus === "yellow") nPlausYellow++;
        // Post-validate: alle „needs"-Cells werden implizit akzeptiert → kein
        // attentionItem mehr. Validate = pauschaler User-OK auf der ganzen Karte.
        if (att.needs && !postValidate) items.push({ day: d, field: f });
      }
    }
    // Post-validate: gleicher Auto-Accept-Block wie attentionItems oben — die
    // Plaus-Counts müssen ebenfalls auf 0 damit AttentionCounter-Detail-Text
    // („31× Plausibilität rot, 2× gelb") verschwindet. Der Breakdown bleibt
    // unverändert (das ist die konfidenz-basierte Verteilung, kein Attention-Marker).
    const plausCounts = postValidate
      ? { red: 0, yellow: 0 }
      : { red: nPlausRed, yellow: nPlausYellow };
    return { breakdown: statusBreakdown(statuses), attentionItems: items, plausCounts };
  }, [days, dayData, cellConfidence, cellKlasse, cellOriginal, thresholds, acceptedCells, postValidate]);

  const firstAttention = attentionItems[0] ?? null;

  // U3: PDF auto-pan target — only set on explicit jump/auto-focus events,
  // NOT on every hover. Hover keeps the activeCell highlight (cheap), but
  // pan-zoom is a heavier interaction reserved for "go work on this cell".
  const [panTarget, setPanTarget] = useState<{ day: number; field: FieldName } | null>(null);

  // U6: focus a specific cell + select its content. Works in BOTH views
  // (Übersicht: input.cell-input, Vergleichen: input.ts-compare-input).
  // Selector matches on data-day + data-field attributes attached to both.
  const focusCell = (day: number, field: FieldName) => {
    setActiveCell({ day, field });
    setPanTarget({ day, field });
    requestAnimationFrame(() => {
      const sel = `[data-day="${day}"][data-field="${field}"] input`;
      const el = document.querySelector<HTMLInputElement>(sel);
      el?.focus();
      el?.select();
      el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
  };

  const jumpToFirstAttention = () => {
    if (firstAttention) focusCell(firstAttention.day, firstAttention.field);
  };

  // Prev/Next navigation across attention items. Wraps around top/bottom.
  const navAttention = (dir: "prev" | "next") => {
    if (attentionItems.length === 0) return;
    if (!activeCell) {
      focusCell(attentionItems[0]!.day, attentionItems[0]!.field);
      return;
    }
    const idx = attentionItems.findIndex(a => a.day === activeCell.day && a.field === activeCell.field);
    const n = attentionItems.length;
    // If active cell isn't in attention list (already accepted): jump to nearest by day.
    let next: AttentionItem;
    if (idx === -1) {
      next = dir === "next"
        ? (attentionItems.find(a => a.day > activeCell.day) ?? attentionItems[0]!)
        : ([...attentionItems].reverse().find(a => a.day < activeCell.day) ?? attentionItems[n - 1]!);
    } else {
      next = attentionItems[(idx + (dir === "next" ? 1 : -1) + n) % n]!;
    }
    focusCell(next.day, next.field);
  };

  // "OK so": mark current cell as accepted (drops from attention list) + jump next.
  const acceptCurrent = () => {
    if (!activeCell) return;
    acceptCell(activeCell.day, activeCell.field);
    // After acceptance the cell is gone from attentionItems; jump next.
    requestAnimationFrame(() => navAttention("next"));
  };

  const serializedData = (): Timesheet["data"] => {
    // Merge statt flatten: nur `cell.value` ist user-mutable; alles andere
    // (confidence, klasse, audit, raw, raw_output etc.) passieren unverändert
    // durch. Verifiziert auf Karte 2ff0f9a9 2026-05-19: alte flatten-Variante
    // verlor `data.meta.pipeline.pre_deskew_*`, `ocr_baseline`, `normalization`,
    // `header.suggestions` + alle per-cell confidence/klasse Felder bei
    // jedem Save irreversibel.
    //
    // Strategie:
    //  1. Top-level Felder kopieren (header, meta, normalization, tier_path,
    //     ocr_baseline, conflict, lookup_shadow, …).
    //  2. days-Dict klonen, dann pro day/field nur den value patchen.
    //  3. Leere Strings → cell auf null setzen (User hat den Wert gelöscht).
    const out: any = { ...((timesheet?.data ?? {}) as any) };
    const sourceDays: Record<string, any> = (out.days ?? {}) as Record<string, any>;
    const days: Record<string, any> = { ...sourceDays };
    for (const [dayStr, d] of Object.entries(dayData)) {
      const dayKey = String(Number(dayStr));
      const prevCells = (days[dayKey] ?? { day: Number(dayStr) }) as Record<string, any>;
      const newCells: Record<string, any> = { ...prevCells };
      for (const f of FIELDS) {
        const v = d[f];
        const existing = (prevCells[f] ?? null) as null | Record<string, unknown> | string;
        if (v && v.length > 0) {
          if (existing && typeof existing === "object") {
            // Preserve confidence/klasse/audit/raw — nur value updaten
            newCells[f] = { ...existing, value: v };
          } else {
            // Frische Zelle, die OCR nie gesehen hat (User trägt manuell ein)
            newCells[f] = { value: v, confidence: null, klasse: null };
          }
        } else {
          // Leer = gelöscht. null statt entfernen, damit das Schema stabil bleibt
          // (Konsumer wie cellStatus erwarten cell als Objekt-oder-null).
          newCells[f] = null;
        }
      }
      days[dayKey] = newCells;
    }
    out.days = days;
    return out as Timesheet["data"];
  };

  const stageEmployee = (employeeId: string) => {
    // Same read-only guard as setField — server would reject anyway.
    const st = timesheet?.state;
    if (st && (st === "calculated" || st === "exported" || st === "archived" || st === "superseded")) {
      return;
    }
    setPendingEmployeeId(employeeId);
    setDirty(true);
  };

  const save = async () => {
    if (!timesheet) return;
    setBusy(true);
    setErr(null);
    try {
      const updated = await api.timesheets.patch(timesheet.id, {
        data: serializedData(),
        employee_id: pendingEmployeeId || null,
      });
      setTimesheet(updated);
      setPendingEmployeeId(updated.employee_id ?? "");
      setDirty(false);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Close = return to background route if we're in modal mode (board/inbox
  // stays mounted under the overlay), else fall back to history.back().
  const closePage = () => {
    // eslint-disable-next-line no-console
    console.log(`[detail ${id?.slice(0, 8)}] closePage`, {
      modal,
      background: background ? `${background.pathname}${background.search}` : null,
      navPath: modal && background ? "nav(background)" : "nav(-1)",
    });
    if (modal && background) {
      nav(background, { replace: false });
    } else {
      nav(-1);
    }
  };

  const validate = async () => {
    if (!timesheet) return;
    // Mandatory Human Quality Gate: ohne Markierungs-Urteil kein Validieren —
    // damit wir aus jedem Urteil (stimmt/falsch) lernen können. Höfliche
    // Aufklärung statt harter Sperre, dann zum gelben Gate-Banner scrollen.
    if (!highlightReview) {
      setGateNotice(
        "Magst du vor dem Validieren kurz die Markierung prüfen? Liegen die farbigen Rahmen im PDF auf den richtigen Tagen/Spalten? Ein Klick auf „Markierung stimmt“ oder „Markierung falsch“ (oben im gelben Feld) genügt — dein Urteil hilft uns, die automatische Erkennung laufend zu verbessern. Danach kannst du validieren.",
      );
      gateRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const tv0 = performance.now();
    // eslint-disable-next-line no-console
    console.log(`[detail ${timesheet.id.slice(0, 8)}] validate START`, { dirty });
    if (dirty) await save();
    setBusy(true);
    setErr(null);
    try {
      await api.timesheets.validate(timesheet.id);
      // UX-Angleich (Thomas 24.07.): NICHT mehr schliessen — die Ansicht
      // bleibt offen, laedt frisch und der validated→calculated-Wechsel
      // erscheint dynamisch (CardDetailActions-Polling, Karte 593).
      // eslint-disable-next-line no-console
      console.log(`[detail ${timesheet.id.slice(0, 8)}] validate DONE → in-place refresh`, { ms: (performance.now() - tv0).toFixed(0) });
      const fresh = await api.timesheets.get(timesheet.id);
      setTimesheet(fresh);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log(`[detail ${timesheet.id.slice(0, 8)}] validate ERROR`, { ms: (performance.now() - tv0).toFixed(0), error: e?.message });
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // #506: die zwei menschlichen Ausgänge aus pending_review.
  //
  // Fix Anzeige-Bug (PM-Status 23.07., Thomas live gefunden): vorher schloss der
  // Erfolgs-Pfad nur die Seite (closePage) — der Board-Poll hinkte nach, die
  // Karte SAH unverändert aus, und ein erneuter Klick lief in den 409
  // („nicht in pending_review"). Jetzt wird die Karte IN PLACE frisch geladen:
  // der neue Zustand ist sofort sichtbar, die Aktionsleiste wechselt
  // state-getrieben, der Button verschwindet → kein Folgeklick-Fehler möglich.
  const reOcr = async () => {
    if (!timesheet) return;
    setBusy(true); setErr(null);
    try {
      await api.timesheets.startOcr(timesheet.id);
      let fresh = await api.timesheets.get(timesheet.id);
      setTimesheet(fresh);
      // Auf das Ende der Lesung warten, dann final aktualisieren (Anlassfall
      // Thomas 23.07.: Status hing sonst auf ocr_pending bis manueller Reload).
      await waitForCardRead("timesheet", timesheet.id);
      fresh = await api.timesheets.get(timesheet.id);
      setTimesheet(fresh);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const approveReview = async () => {
    if (!timesheet) return;
    setBusy(true); setErr(null);
    try {
      await api.cards.approveReview("timesheet", timesheet.id, "geprüft, freigegeben");
      const fresh = await api.timesheets.get(timesheet.id);
      setTimesheet(fresh);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };

  const onBack = () => {
    if (modal) {
      // In modal mode, ESC + backdrop + ✕ are handled by DetailModal with the
      // dirty-confirm dialog. The "← Zurück" button mirrors that flow.
      // The DetailModal close logic is wrapped in `closePage` via the dirty
      // gate at the modal layer; here we just trigger the same.
      if (dirty && !window.confirm("Ungespeicherte Änderungen verwerfen?")) return;
      closePage();
      return;
    }
    if (dirty && !window.confirm("Ungespeicherte Änderungen. Wirklich zurück? Änderungen gehen verloren.")) return;
    nav(-1);
  };

  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  if (err && !timesheet) return <div style={{ padding: 24 }}>Fehler: {err}</div>;
  if (!timesheet) return <div style={{ padding: 24 }}>Lade…</div>;

  // T13: 3g-Zulagenblatt — anderes data-Shape (days[] mit zulagen-Objekt statt
  // von1/bis1-Zellen). Die Ergebnis-Hälfte rendert die Zulagen-Tages-Tabelle,
  // read-only; die Stundenlisten-Mechanik (Compare, Gates, Validieren) greift
  // hier nicht. Detection über manual_doc_type ODER meta.doc_type — der
  // Worker-Erfolgs-Merge ersetzt data (manual_doc_type weg, meta.doc_type da),
  // der fail-loud-Pfad merged (manual_doc_type da, meta.doc_type fehlt).
  // #466: registry-getrieben — die View kommt aus dem doc_type der Karte
  // (docType→View-Registry), nicht aus data-shape-sniffing.
  // ⚠ EINBETTUNGS-MUSTER (docs/konzept/produkt/40-detail-review.md REV-19): dies ist ein
  // FRÜHER RETURN vor dem Edit-Rahmen unten (save/READONLY_STATES) → die View erbt KEIN
  // Edit/Save/Highlight und ist read-only PER KONSTRUKTION. Ein editierbarer neuer Doc-Typ
  // (z.B. Fahrtenbuch #521) darf so NICHT gebaut werden — er muss IN den Rahmen eingebettet
  // werden (Muster 1), sonst wird er still read-only.
  if (timesheetViewFor(timesheet.data) === "zulagenblatt") {
    const zInner = (
      <ZulagenblattDetail
        timesheet={timesheet}
        modal={modal}
        onBack={onBack}
        onRefresh={async () => {
          const fresh = await api.timesheets.get(timesheet.id);
          setTimesheet(fresh);
        }}
        /* Karte 591: Markierung-prüfen-Gate — dieselbe Urteil-Mechanik
           (sendHighlightVerdict oben, POST /:id/highlight-review) wie die
           Stundenliste; die View rendert nur die Banner + den Übernehmen-Guard. */
        highlightReview={highlightReview}
        verdictBusy={verdictBusy}
        verdictErr={verdictErr}
        sendHighlightVerdict={sendHighlightVerdict}
      />
    );
    if (modal) {
      return (
        <DetailModal dirty={false} onClose={closePage}>
          {zInner}
        </DetailModal>
      );
    }
    return zInner;
  }

  // Read-only-Modus ab calculated. Server-Gate (hub/src/routes/timesheets.ts
  // PATCH /:id) lehnt Updates in diesen States mit 409 card_read_only ab —
  // die UI muss konsistent sein, sonst kann der User scheinbar editieren bis
  // er auf "Speichern" drückt und einen Fehler bekommt (Glass-Box-Bruch).
  const READONLY_STATES = new Set(["calculated", "exported", "archived", "superseded"]);
  const readOnly = READONLY_STATES.has(timesheet.state);
  // Sobald Calc-Daten vorliegen, rendern Berechnung + Tages-Tabelle als Flex-
  // Geschwister → der editierbare Übersichts-Body (flex:1/min-height:0) kollabiert
  // auf kürzeren Schirmen auf 0 (PDF + Eingaben weg). Min-Höhe daher auch hier
  // greifen lassen, nicht nur bei readOnly. (2026-06-22, Glass-Box-Befund Ardelean.)
  const hasCalc = !!(timesheet.data as any)?.calculation;
  // `busy` triggert disable-of-inputs during async ops; lockedForEdit kombiniert
  // das mit dem permanenten readOnly-State.
  const lockedForEdit = busy || readOnly;

  // Vererbte Detail-Basis (2026-07-13): Kopf/Body/Footer aus DetailViewShell.
  // Rein strukturell — identisches DOM (.ts-detail / .ts-detail-header /
  // .ts-detail-meta). Editier-/Gate-/Compare-Logik im Body bleibt unangetastet.
  const tsTitle = (
    <>
      Timesheet · {timesheet.month}
      {!readOnly && dirty && " · *ungespeichert*"}
      {readOnly && (
        <span style={{
          marginLeft: 12, fontSize: 13, fontWeight: 600,
          padding: "2px 8px", borderRadius: 4,
          background: "#fef3c7", color: "#92400e",
          border: "1px solid #fcd34d",
        }} title={`Karte im state=${timesheet.state} ist nicht editierbar. Server lehnt PATCH mit 409 ab.`}>
          🔒 Read-only ({timesheet.state})
        </span>
      )}
    </>
  );
  const tsSource = (timesheet as any).pdf_orig_filename ? (
    <>
      <strong>Quelle:</strong>{" "}
      <a
        href={api.timesheets.pdfUrl(timesheet.id)}
        target="_blank"
        rel="noreferrer"
        title="Original-PDF öffnen"
        style={{ color: "#1d4ed8" }}
      >
        {(timesheet as any).pdf_orig_filename}
      </a>
      {typeof (timesheet as any).pdf_bytes === "number" && (
        <span> · {((timesheet as any).pdf_bytes / 1024).toFixed(0)} KB</span>
      )}
      <span> · <CardIdChip id={timesheet.id} /></span>
    </>
  ) : null;
  const tsMeta = (
    <>
          <label className="field-inline">
            <span>Mitarbeiter</span>
            <select
              value={pendingEmployeeId}
              onChange={(e) => stageEmployee(e.target.value)}
              disabled={lockedForEdit}
              title={readOnly ? `Karte ist read-only (state=${timesheet.state})` : undefined}
            >
              <option value="">— wählen —</option>
              {/* Mitarbeiter ohne work_schedule können nicht zugeordnet werden —
                  Soll-Zeiten sind mandatory für die Berechnung. Konfiguration
                  in Einstellungen → Mitarbeiter. Aktuelle Auswahl bleibt sichtbar
                  damit Birgit sieht falls der Mitarbeiter umkonfiguriert wurde. */}
              {employees
                .filter((e) => e.work_schedule || e.id === pendingEmployeeId)
                .map((e) => (
                  <option key={e.id} value={e.id} disabled={!e.work_schedule && e.id !== pendingEmployeeId}>
                    {e.last_name}, {e.first_name} ({e.dienstnehmernummer})
                    {!e.work_schedule ? " — ⚠ keine Soll-Zeiten" : ""}
                  </option>
                ))}
            </select>
          </label>
          <span className="ts-state">Status: <strong>{timesheet.state}</strong></span>
          {viewport !== "mobile" && (
            <div className="ts-viewmode-toggle" role="tablist" aria-label="Ansichtsmodus" style={{
              display: "flex", border: "1px solid #ccc", borderRadius: 4, overflow: "hidden",
            }}>
              <button type="button" role="tab" aria-selected={viewMode === "overview"}
                      onClick={() => setViewModePersist("overview")}
                      title="PDF + Tabelle nebeneinander — Vogelperspektive für Quick-Scan"
                      style={{
                        padding: "4px 12px", border: 0,
                        background: viewMode === "overview" ? "#2563eb" : "#f4f4f4",
                        color: viewMode === "overview" ? "#fff" : "#333", cursor: "pointer", fontSize: 13,
                      }}>Übersicht</button>
              <button type="button" role="tab" aria-selected={viewMode === "compare"}
                      onClick={() => highlightApproved && setViewModePersist("compare")}
                      disabled={!highlightApproved}
                      title={highlightApproved
                        ? "Pro Zelle: PDF-Crop + OCR-Wert nebeneinander — Detail-Vergleich"
                        : "Gesperrt — erst in der Übersicht „Markierung stimmt“ freigeben (Human Quality Gate)"}
                      style={{
                        padding: "4px 12px", border: 0, borderLeft: "1px solid #ccc",
                        background: viewMode === "compare" ? "#2563eb" : "#f4f4f4",
                        color: viewMode === "compare" ? "#fff" : highlightApproved ? "#333" : "#9ca3af",
                        cursor: highlightApproved ? "pointer" : "not-allowed", fontSize: 13,
                      }}>{highlightApproved ? "Vergleichen" : "🔒 Vergleichen"}</button>
            </div>
          )}
    </>
  );

  const inner = (
    <DetailViewShell
      modal={modal}
      onBack={onBack}
      title={tsTitle}
      source={tsSource}
      meta={tsMeta}
    >

      {!pendingEmployeeId && ((timesheet.data as any)?.header?.suggestions ?? []).length > 0 && (
        <div className="suggestions-row">
          <span>OCR-Vorschlag:</span>
          {((timesheet.data as any).header.suggestions as Array<{ id: string; name: string; score: number }>).map((s) => (
            <button
              key={s.id}
              className="suggestion-chip"
              onClick={() => stageEmployee(s.id)}
              disabled={lockedForEdit}
              title={readOnly ? `Karte ist read-only (state=${timesheet.state})` : undefined}
            >
              {s.name} <span className="score">({Math.round(s.score * 100)}%)</span>
            </button>
          ))}
        </div>
      )}

      {(timesheet.data as any)?.ocr_error &&
       String((timesheet.data as any).ocr_error).includes("WRONG_FORM_TYPE") ? (
        <div style={{
          background: "#eff6ff", border: "1px solid #3b82f6", borderLeft: "4px solid #3b82f6",
          padding: "12px 16px", borderRadius: 4, marginBottom: 8,
        }}>
          <div style={{ fontWeight: 600, color: "#1e3a8a", marginBottom: 4 }}>
            🧳 Falscher Formulartyp — das ist eine Diäten-Abrechnung
          </div>
          <div style={{ fontSize: 13, color: "#1e40af", marginBottom: 10 }}>
            Diese Seite wurde als <strong>Stundenliste</strong> hochgeladen, ist aber
            ein <strong>Diäten-Formular</strong>. Mit einem Klick wandelst du sie in
            eine Diäten-Abrechnung um — sie wird dann automatisch korrekt ausgelesen.
          </div>
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={async () => {
              if (!confirm("Diese Karte als Diäten-Abrechnung verarbeiten?")) return;
              setBusy(true);
              setErr(null);
              try {
                const r = await fetch(
                  new URL(`api/timesheets/${timesheet.id}/reclassify`, document.baseURI).toString(),
                  { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ target: "expense" }) },
                );
                if (!r.ok) throw new Error(humanServerError(await r.text()));
                const body = await r.json();
                if (body.expense_id) nav(`/expenses/${body.expense_id}`);
                else closePage();
              } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
            }}
          >🧳 Als Diäten verarbeiten</button>
        </div>
      ) : (timesheet.data as any)?.ocr_error && (
        <div className="warning-box">
          OCR-Fehler: <code>{(timesheet.data as any).ocr_error}</code> — Werte manuell
          eintragen. Speichern + Validieren setzt den Status unabhängig von OCR.
        </div>
      )}

      {/* Human Quality Gate (glassbox-pipeline-pattern.md §7b): Urteil über die
       *  Markierungs-Qualität fällt in der ÜBERSICHT (ganzes PDF + alle Rahmen
       *  sichtbar). Vergleichen bleibt gesperrt bis zur Freigabe — am kleinen
       *  Zell-Crop ist ein verrutschtes Raster nicht erkennbar. */}
      {verdictErr && (
        <div style={{
          background: "#fef2f2", border: "1px solid #dc2626", borderRadius: 4,
          padding: "8px 12px", fontSize: 13, color: "#7f1d1d", marginBottom: 8,
        }}>
          ⚠ Markierungs-Urteil fehlgeschlagen: {verdictErr}
        </div>
      )}
      {viewport !== "mobile" && viewMode === "overview" && (
        highlightReview === null ? (
          <div ref={gateRef} style={{
            background: "#fffbeb", border: "1px solid #f59e0b", borderLeft: "4px solid #f59e0b",
            padding: "10px 14px", borderRadius: 4, fontSize: 13, color: "#92400e",
            display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 8,
          }}>
            <div style={{ flex: "1 1 320px" }}>
              <strong>Markierung prüfen (Human Quality Gate)</strong>
              <div style={{ marginTop: 4 }}>
                Liegen die Markierungs-Rahmen im PDF auf den richtigen Tagen/Spalten?
                Der Vergleichen-Modus bleibt gesperrt, bis du freigibst — dein Urteil
                wird gespeichert, damit wir aus Markierungs-Fehlern lernen.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="primary success" disabled={verdictBusy}
                      onClick={() => sendHighlightVerdict("approved")}>
                ✓ Markierung stimmt — Vergleichen freigeben
              </button>
              <button type="button" className="primary" disabled={verdictBusy}
                      style={{ background: "#dc2626" }}
                      onClick={() => sendHighlightVerdict("blocked")}>
                ✗ Markierung falsch — sperren
              </button>
            </div>
          </div>
        ) : highlightApproved ? (
          <div style={{
            fontSize: 12, color: "#166534", background: "#f0fdf4",
            border: "1px solid #bbf7d0", borderRadius: 4, padding: "6px 10px",
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8,
          }}>
            <span>
              ✓ Markierung freigegeben durch {highlightReview?.by_name ?? "User"}
              {highlightReview?.at ? ` am ${new Date(highlightReview.at).toLocaleString("de-AT")}` : ""}
              {" "}— Vergleichen ist verifiziert
            </span>
            <button type="button" className="link" disabled={verdictBusy}
                    title="Markierung passt doch nicht — Vergleichen sperren (wird gespeichert)"
                    onClick={() => sendHighlightVerdict("blocked")}>
              Sperren
            </button>
          </div>
        ) : (
          <div style={{
            fontSize: 12, color: "#7f1d1d", background: "#fef2f2",
            border: "1px solid #fecaca", borderRadius: 4, padding: "6px 10px",
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8,
          }}>
            <span>
              ❌ Markierung gesperrt durch {highlightReview?.by_name ?? "User"}
              {highlightReview?.at ? ` am ${new Date(highlightReview.at).toLocaleString("de-AT")}` : ""}
              {" "}— Vergleichen deaktiviert (gespeichert; Re-OCR stellt das Gate neu)
            </span>
            <button type="button" className="link" disabled={verdictBusy}
                    onClick={() => sendHighlightVerdict("approved")}>
              Doch freigeben
            </button>
          </div>
        )
      )}

      {/* Desktop: BEIDE Views immer mounted, Switch via CSS-display.
       *  Begründung: jeder Mount lädt PDF-Preview + grid-bboxes + Canvas-
       *  overlay neu — User sah beim Tab-Wechsel das PDF und Highlighting
       *  jedes Mal neu rendern. Mit display-toggle bleibt state + DOM
       *  warm, Wechsel ist instant.
       *  Mobile rendert nur den Overview-Pfad (kein Compare-Modus). */}
      {viewport !== "mobile" && compareEverActivated && (
        <div className="ts-compare-body" style={{ display: viewMode === "compare" ? "block" : "none" }}>
          <AttentionCounter
            breakdown={breakdown}
            plausCounts={plausCounts}
            attentionItemsCount={attentionItems.length}
            cardState={timesheet.state}
            onJump={jumpToFirstAttention}
            onPrev={() => navAttention("prev")}
            onNext={() => navAttention("next")}
            onAcceptCurrent={acceptCurrent}
            hasActive={!!activeCell}
          />
          <CompareView
            days={days}
            timesheetId={timesheet.id}
            dayData={dayData}
            cellConfidence={cellConfidence}
            cellKlasse={cellKlasse}
            cellOriginal={cellOriginal}
            thresholds={thresholds}
            setField={setField}
            lockedForEdit={lockedForEdit}
            postValidate={postValidate}
            activeCell={activeCell}
            onCellHover={setActiveCell}
          />
        </div>
      )}
      <div
        ref={viewport !== "mobile" ? split.bodyRef : undefined}
        className={`ts-detail-body ${viewport === "mobile" ? "ts-mobile" : ""}`}
        style={viewport !== "mobile"
          ? {
              position: "relative",
              display: viewMode === "overview" ? "grid" : "none",
              gridTemplateColumns: `${split.pct}% 7px 1fr`,
              // Read-only (calculated/exported/…): Berechnung + Tages-Tabelle
              // kommen als Flex-Geschwister dazu → der flex:1/min-height:0-Body
              // schrumpfte sonst auf 0 und der Scan verschwand (Regression).
              // Feste Mindesthöhe hält die PDF-Vorschau OBERHALB der Berechnung.
              ...((readOnly || hasCalc) ? { minHeight: "78vh", flex: "none" as const } : {}),
            }
          : { position: "relative" }}
      >
        {!previewReady && (
          <div className="page-loading-curtain" role="status" aria-live="polite">
            <div className="spinner" />
            <div className="label">📄 Seite wird geladen …</div>
            <div className="sub">Bitte warten — die Markierungen werden auf das PDF ausgerichtet. Noch nichts eintragen.</div>
          </div>
        )}
        <PdfWithHighlights
          imageUrl={new URL(`api/cards/timesheet/${timesheet.id}/preview-cached`, document.baseURI).toString()}
          pdfHrefForOpen={api.timesheets.pdfUrl(timesheet.id)}
          daysInMonth={days}
          activeCell={activeCell}
          onCellHover={setActiveCell}
          onReady={() => setPreviewReady(true)}
          /* Mobile: disable interactive overlays. The sticky PDF (only ~240px
           * tall) overlaps day-cards in z-stacking, intercepting taps that
           * should land on the input below. Each row is also too small (~7px)
           * for finger precision. PDF stays as visual reference only. */
          interactive={viewport !== "mobile"}
          gridUrl={new URL(`api/cards/timesheet/${timesheet.id}/grid-bboxes`, document.baseURI).toString()}
          panToCell={panTarget}
        />
        {viewport !== "mobile" && (
          <div
            className="ts-split-handle"
            onPointerDown={split.startDrag}
            role="separator"
            aria-orientation="vertical"
            title="Ziehen: Scan/Tabelle breiter oder schmäler machen"
          />
        )}
        <div className="ts-data">
          <AttentionCounter
            breakdown={breakdown}
            plausCounts={plausCounts}
            attentionItemsCount={attentionItems.length}
            cardState={timesheet.state}
            onJump={jumpToFirstAttention}
            onPrev={() => navAttention("prev")}
            onNext={() => navAttention("next")}
            onAcceptCurrent={acceptCurrent}
            hasActive={!!activeCell}
          />
          <div className="ts-data-hint">
            Werte manuell eintragen oder auf OCR warten. HH:MM Format.
            Zwei unabhängige Signale pro Zelle:
            {" "}<strong>Konfidenz</strong> (
            <span className="legend-dot legend-korr" /> korrigieren ·
            {" "}<span className="legend-dot legend-pruef" /> prüfen ·
            {" "}<span className="legend-dot legend-auto" /> auto-repariert ·
            {" "}<span className="legend-dot legend-ign" /> ignoriert
            )
            {" · "}<strong>Plausibilität</strong> (! rot = atypische Minute, Reihenfolge-Verstoß
            oder Symbol/Zeit-Konflikt · ? gelb = 5-Min-Raster).
            Unmarkierte Zellen sind OK.
          </div>
          {viewport === "mobile" ? (
            <DayCardsList
              days={days}
              dayData={dayData}
              cellConfidence={cellConfidence}
              cellOriginal={cellOriginal}
              suggestions={suggestions}
              setField={setField}
              lockedForEdit={lockedForEdit}
              postValidate={postValidate}
              timesheetId={timesheet.id}
            />
          ) : (
            <table className="ts-table" ref={tableRef}>
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>von1</th>
                  <th>bis1</th>
                  <th>von2</th>
                  <th>bis2</th>
                  <th>Symbol</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: days }, (_, i) => i + 1).map((day) => {
                  const d = dayData[day] ?? {};
                  const conf = cellConfidence[day] ?? {};
                  const orig = cellOriginal[day] ?? {};
                  const klassen = cellKlasse[day] ?? {};
                  return (
                    <tr key={day}>
                      <td className="day-col">{day}</td>
                      {FIELDS.map((f) => {
                        const bucket = bucketOf(conf[f] ?? null);
                        const current = d[f] ?? "";
                        // Live re-classify if Birgit changed the value (else use stored klasse)
                        const liveKlasse: Klasse | undefined = current !== (orig[f] ?? "")
                          ? classify(current, f).klasse
                          : klassen[f];
                        const status: CellStatus = cellStatus(liveKlasse, conf[f], current, thresholds);
                        const csClass = STATUS_CLASS[status];
                        // Plausibility — same helper as Compare-View. Shared signal.
                        // Post-Validate: gleicher Auto-Accept wie AttentionCounter →
                        // Plaus-Badges + cell-bg-tinting verschwinden, sonst Drift
                        // zw. "alle bestätigt"-Text und visuellen !/?-Markern.
                        const plausResult = cellPlausibility(f as any, current, d as any);
                        const plaus = postValidate ? "ok" : plausResult.severity;
                        const plausCls = plaus === "red" ? "ts-plaus-red"
                                      : plaus === "yellow" ? "ts-plaus-yellow" : "";
                        const plausTooltip = plausResult.reasons.join("\n");
                        const scope = `days.*.${f}`;
                        const listId = `sugg-${f}`;
                        const sugList = suggestions[scope] ?? [];
                        const isActive = activeCell?.day === day && activeCell?.field === f;
                        // Excel-style navigation. Tab keeps the U6 attention-
                        // skip behaviour (jump to next cs-korrigieren/pruefen).
                        // Arrow Up/Down always move row (FIELDS.length step).
                        // Arrow Left/Right move cell only when caret at edge of
                        // the input (so within-text cursor moves still work).
                        // Enter = next row (Excel default).
                        const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
                          if (!tableRef.current) return;
                          const all = Array.from(
                            tableRef.current.querySelectorAll<HTMLInputElement>(
                              "input.cell-input",
                            ),
                          );
                          const idx = all.indexOf(e.currentTarget);
                          if (idx < 0) return;
                          const cols = FIELDS.length;
                          const focusAt = (n: number) => {
                            const t = all[n];
                            if (t) { t.focus(); t.select(); }
                          };
                          if (e.key === "Tab") {
                            const dir = e.shiftKey ? -1 : 1;
                            for (let i = idx + dir; i >= 0 && i < all.length; i += dir) {
                              const cls = all[i]!.parentElement?.className ?? "";
                              if (cls.includes("cs-korrigieren") || cls.includes("cs-pruefen")) {
                                e.preventDefault();
                                all[i]!.focus();
                                all[i]!.select();
                                return;
                              }
                            }
                            return;
                          }
                          if (e.key === "ArrowUp") {
                            e.preventDefault();
                            focusAt(idx - cols);
                            return;
                          }
                          if (e.key === "ArrowDown" || e.key === "Enter") {
                            e.preventDefault();
                            focusAt(idx + cols);
                            return;
                          }
                          if (e.key === "ArrowLeft") {
                            const inp = e.currentTarget;
                            if (inp.selectionStart === 0 && inp.selectionEnd === 0) {
                              e.preventDefault();
                              focusAt(idx - 1);
                            }
                            return;
                          }
                          if (e.key === "ArrowRight") {
                            const inp = e.currentTarget;
                            const end = inp.value.length;
                            if (inp.selectionStart === end && inp.selectionEnd === end) {
                              e.preventDefault();
                              focusAt(idx + 1);
                            }
                            return;
                          }
                        };
                        return (
                          <td
                            key={f}
                            className={`conf-${bucket} ${csClass} ${plausCls}${isActive ? " active" : ""}`}
                            data-day={day}
                            data-field={f}
                            data-cs={status}
                            onPointerEnter={() => setActiveCell({ day, field: f })}
                            onPointerLeave={() => setActiveCell(null)}
                          >
                            <input
                              type="text"
                              className="cell-input"
                              value={current}
                              list={sugList.length > 0 ? listId : undefined}
                              onChange={(e) => {
                                // Symb-field: instant uppercase as you type
                                // (p→P, za→ZA, pflege→PFLEGE). Time-fields:
                                // raw — user might be mid-typing "08:" etc.
                                const v = f === "symb" ? e.target.value.toUpperCase() : e.target.value;
                                setField(day, f, v);
                              }}
                              onKeyDown={onKeyDown}
                              onFocus={(e) => {
                                // Click overwrites directly — next keystroke replaces existing value.
                                e.currentTarget.select();
                                setActiveCell({ day, field: f });
                              }}
                              onBlur={() => {
                                // Auto-format on blur:
                                // - time-fields: 0800 → 08:00, 830 → 08:30, 8 → 08:00
                                // - symb-field: alias → canonical via classify()
                                //   (Pflegeurlaub → P, KRANK → K, urlaub → U).
                                //   Falls back to plain trim+upper for unknown
                                //   so "Garbage" still shows as "GARBAGE".
                                let formatted = current;
                                if (isTimeField(f)) {
                                  formatted = formatTimeInput(current);
                                } else if (f === "symb") {
                                  const r = classify(current, "symb");
                                  formatted = r.normalized ?? current.trim().toUpperCase();
                                }
                                if (formatted !== current) {
                                  setField(day, f, formatted);
                                }
                                // Correction-Tracking läuft seit Migration 034
                                // server-side im PATCH /api/timesheets/:id-Handler
                                // (mergeCardData → corrections[]). Frontend muss
                                // nichts mehr explizit melden.
                              }}
                              disabled={lockedForEdit}
                              placeholder={f === "symb" ? "U/K/ZA/P/F…" : "HH:MM"}
                            />
                            {plaus !== "ok" && (
                              <span className={`ts-plaus-badge ts-plaus-badge-${plaus} ts-tooltip-trigger ts-plaus-badge-overlay`}
                                    aria-label={plausTooltip}>
                                {plaus === "red" ? "!" : "?"}
                                <span className="ts-tooltip" role="tooltip">{plausTooltip}</span>
                              </span>
                            )}
                            {sugList.length > 0 && (
                              <datalist id={listId}>
                                {sugList.map((s) => (
                                  <option key={s.value} value={s.value} />
                                ))}
                              </datalist>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <ProgressTracker breakdown={breakdown} />
        </div>
      </div>

      {(timesheet.data as any)?.calculation && (
        <div className="calc-panel">
          <h3>Berechnung</h3>
          <dl className="kv-grid">
            <dt>Ist-Stunden (Monat)</dt>
            <dd>{readCalcTotal(timesheet.data, "stunden_ist", "work_hours")} h</dd>
            <dt>Soll-Stunden (Monat)</dt>
            <dd>{readCalcTotal(timesheet.data, "stunden_soll", null)} h</dd>
            <dt>Δ Ist−Soll</dt>
            <dd>{readCalcTotal(timesheet.data, "diff_h", null)} h</dd>
            <dt>Krank / Urlaub / Feiertag</dt>
            <dd>
              {readCalcTotal(timesheet.data, "tage_K", "krank_days")} / {" "}
              {readCalcTotal(timesheet.data, "tage_U", "urlaub_days")} / {" "}
              {readCalcTotal(timesheet.data, "tage_F", "feiertag_days")} Tage
            </dd>
            <dt>Zeitausgleich konsumiert</dt>
            <dd>{readCalcTotal(timesheet.data, "stunden_ZA_konsumiert", "zeitausgleich_hours")} h</dd>
            <dt>Verwendete Wochen-Überstundengrenze</dt>
            <dd>{(timesheet.data as any).calculation.inputs?.weekly_ueberstunden_threshold_h ?? 42} h · {(timesheet.data as any).calculation.inputs?.weekly_threshold_source === "employee" ? "Individuell" : "Standard / bisheriger Berechnungsstand"}</dd>
            <dt>ZA-Saldo ohne separat ausgewiesene Überstunden</dt>
            <dd>{Array.isArray((timesheet.data as any).calculation.weeks) ? `${zaWochenSaldo((timesheet.data as any).calculation.weeks)} h` : "Im alten Berechnungsformat nicht gesondert ausgewiesen"}</dd>
            <dt>Positive Mehrstunden nach Überstunden-Aufteilung</dt>
            <dd>
              {readCalcTotal(timesheet.data, "mehrstunden_total_h", null)} h
              <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>
                → ZA-Konto (immer)
              </span>
            </dd>
            <dt>Überstunden 50% (Wochen- und Tagesregel)</dt>
            <dd>
              {readCalcTotal(timesheet.data, "ueberstunden_50_total_h", null)} h
              {(() => {
                const emp = employees.find(e => e.id === pendingEmployeeId);
                const mode = emp?.ueberstunden_pay_mode;
                if (!mode) return null;
                return (
                  <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>
                    → {mode === "cash" ? "Cash-Auszahlung" : "ZA-Konto"} (MA-Setting <code>{mode}</code>)
                  </span>
                );
              })()}
            </dd>
            <dt>Σ Monatsaufteilung (RZL)</dt>
            <dd>
              Auszahlung: <strong>{readCalcTotal(timesheet.data, "ueberstunden_cash_h", null)} h</strong>
              {" · "}ZA-Gesamt (je Zahlweise einschließlich Ü50): <strong>{zaKontoWieExport(
                (timesheet.data as any).calculation,
                employees.find((e) => e.id === pendingEmployeeId)?.ueberstunden_pay_mode,
              ) ?? readCalcTotal(timesheet.data, "ueberstunden_za_h", null)} h</strong>
            </dd>
          </dl>
          {(() => {
            const warns: string[] = (timesheet.data as any).calculation.warnings ?? [];
            if (warns.length === 0) return null;
            // Post-Validate: User hat alle Hinweise pauschal akzeptiert (siehe
            // AttentionCounter all_done). Liste in <details> einklappen damit
            // sie nicht visuell als offene Aktionen erscheint, aber als Audit-
            // Quelle abrufbar bleibt. Gelbe Warnbox NICHT verwenden — das
            // suggeriert offene Aufgaben.
            if (postValidate) {
              return (
                <details style={{
                  marginTop: 8, padding: 8, borderRadius: 4,
                  background: "#f9fafb", border: "1px solid #e5e7eb",
                  fontSize: 12, color: "#374151",
                }}>
                  <summary style={{ cursor: "pointer", color: "#6b7280" }}>
                    {warns.length} Hinweise wurden bei der Berechnung berücksichtigt — vom User akzeptiert (Audit-Sicht)
                  </summary>
                  <div style={{ marginTop: 8 }}>
                    {warns.map((w, i) => (
                      <div key={i} style={{ fontSize: 12, color: "#6b7280" }}>· {w}</div>
                    ))}
                  </div>
                </details>
              );
            }
            return (
              <div className="warning-box">
                {warns.map((w, i) => (
                  <div key={i}>⚠ {w}</div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {(() => {
        // Tages-Tabelle (2026-05-22): identische Komponente wie im Werkstatt-
        // Step 16 — damit Audit und User dieselbe Sicht haben. Nur sichtbar
        // wenn data.calculation.days vorliegt (also Berechnung erfolgreich war).
        const days = (timesheet.data as any)?.calculation?.days;
        if (!days || days.length === 0) return null;
        return (
          <div className="calc-panel" style={{ marginTop: 16 }}>
            <h3>Tages-Tabelle</h3>
            <p className="muted" style={{ marginTop: -8, marginBottom: 12, fontSize: 12 }}>
              Tag-für-Tag-Aufstellung der Berechnung. Zeilen mit Hinweisen
              sind farblich markiert — gelb = zu prüfen, rot = blockierend.
            </p>
            <div style={{ overflowX: "auto" }}>
              <DayTable days={days} />
            </div>
          </div>
        );
      })()}

      {(() => {
        // Banner "Geteilte Woche über Monatswechsel" (2026-05-22).
        // Wird ab Mai 2026 relevant: wenn der 1. eines Monats nicht Mo ist,
        // gehört die ISO-Woche TEIL-weise zum Vormonat. Mehrstunden + 50%-
        // Überstunden werden pro Woche bewertet — ohne Vormonats-Tage ist die
        // erste Woche unterbewertet. Banner reagiert LIVE auf Vormonats-State,
        // d.h. wenn März später dazukommt ändert sich der Text automatisch.
        const cm = (timesheet as any).cross_month_info as import("../lib/api").CrossMonthInfo | undefined;
        if (!cm || cm.state === "no_split" || cm.state === "ok") return null;
        const isMissing = cm.state === "missing_prev_data";
        const prevMonthLabel = (() => {
          const [yStr, mStr] = timesheet.month.split("-");
          const y = Number(yStr ?? "0");
          const m = Number(mStr ?? "0");
          const py = m === 1 ? y - 1 : y;
          const pm = m === 1 ? 12 : m - 1;
          return `${py}-${String(pm).padStart(2, "0")}`;
        })();
        return (
          <div
            className="warning-box"
            style={{
              background: isMissing ? "#fffbeb" : "#fff7ed",
              border: `1px solid ${isMissing ? "#f59e0b" : "#fb923c"}`,
              borderLeft: `4px solid ${isMissing ? "#f59e0b" : "#fb923c"}`,
              color: isMissing ? "#92400e" : "#7c2d12",
              padding: 12,
              margin: "8px 0",
              borderRadius: 4,
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {isMissing ? (
              <>
                ⚠ <strong>Geteilte Woche KW{cm.first_week_iso}:</strong>{" "}
                Diese ISO-Woche beginnt im Vormonat ({prevMonthLabel}),
                aber für diesen Monat liegt keine exportierte Karte vor.
                Mehrstunden + 50%-Überstunden in KW{cm.first_week_iso} wurden
                <strong> ohne die Vormonats-Tage </strong>
                berechnet — bei Bedarf manuell ausgleichen.
                {cm.prev_card && (
                  <>
                    {" "}(Vormonats-Karte existiert mit state{" "}
                    <code>{cm.prev_card.state}</code>, aber sie ist nicht exported.)
                  </>
                )}
                <div style={{ marginTop: 8 }}>
                  <strong>Was zu tun ist:</strong> Sobald die Vormonats-Karten
                  exportiert sind, hier <strong>↻ Neu berechnen</strong> (in der
                  Monatsübersicht) und das Monats-CSV neu ziehen — dann fließen die
                  Vormonats-Tage der ersten Woche mit ein.
                </div>
              </>
            ) : (
              <>
                ⚠ <strong>Geteilte Woche KW{cm.first_week_iso} — neue Vormonats-Daten:</strong>{" "}
                Im Vormonat ({prevMonthLabel}) gibt es seit dieser Berechnung
                <strong> neue Datensätze</strong> die in KW{cm.first_week_iso}
                noch nicht berücksichtigt wurden. Bei einem Re-Export würde
                die Bewertung der ersten Woche andere Werte ergeben.
                {cm.prev_card && (
                  <>
                    {" "}Aktueller Stand der Vormonats-Karte:{" "}
                    <code>{cm.prev_card.state}</code>, zuletzt aktualisiert{" "}
                    <code>{cm.prev_card.updated_at.slice(0, 16).replace("T", " ")}</code>.
                  </>
                )}
                <div style={{ marginTop: 8 }}>
                  <strong>Was zu tun ist:</strong> In der Monatsübersicht auf{" "}
                  <strong>↻ Neu berechnen</strong> klicken und anschließend das{" "}
                  <strong>Monats-CSV neu exportieren</strong> — dann übernimmt die
                  erste Woche die aktuellen Vormonats-Werte und dieser Hinweis
                  verschwindet. Es passiert bewusst nichts automatisch, damit nichts
                  unbemerkt überschrieben wird. Hängt ein weiterer Monat daran (auch
                  dort beginnt der 1. nicht am Montag) und ist bereits
                  berechnet/exportiert, bitte auch ihn neu berechnen.
                </div>
              </>
            )}
          </div>
        );
      })()}

      {(timesheet as any).calc_stale && (
        <div
          className="warning-box"
          style={{
            background: "#fffbeb",
            border: "1px solid #f59e0b",
            borderLeft: "4px solid #f59e0b",
            color: "#92400e",
            padding: 12,
            margin: "8px 0",
            borderRadius: 4,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          ⚠ <strong>Berechnung veraltet:</strong> Mitarbeiter-Einstellungen
          (Sollzeit, Auszahlungs-Modus oder Wochen-Überstundengrenze) wurden nach der Berechnung
          geändert. {timesheet.weekly_threshold_stale
            ? `Aktuelle Wochen-Grenze: ${timesheet.effective_weekly_threshold?.hours} h. Ein normaler Export ist bis zur geprüften Neuberechnung gesperrt.`
            : "Beim Export würde RZL die alten Werte schreiben."}{" "}
          <strong>Empfohlen:</strong>{" "}
          <em>↻ Zurück zur Bearbeitung</em> → <em>Validieren</em> für aktuelle Werte.
        </div>
      )}
      {/* Base-Template (2026-06-10): Reopen/Export/Re-Export/Revert kommen aus
          CardDetailActions — identische Standard-Funktionalität wie Diäten.
          State-driven: der User sieht NIE einen Knopf den der Server ablehnt. */}
      <CardDetailActions
        kind="timesheet"
        cardId={timesheet.id}
        month={timesheet.month}
        clientId={timesheet.client_id}
        state={timesheet.state}
        busy={busy}
        err={err}
        setBusy={setBusy}
        setErr={setErr}
        onRefresh={async () => {
          const fresh = await api.timesheets.get(timesheet.id);
          setTimesheet(fresh);
        }}
      >
        {!readOnly && (
          <>
            {gateNotice && (
              <div style={{
                flexBasis: "100%", background: "#eff6ff", border: "1px solid #bfdbfe",
                borderLeft: "4px solid #3b82f6", borderRadius: 6, padding: "10px 14px",
                fontSize: 13, color: "#1e3a8a", marginBottom: 8, lineHeight: 1.5,
              }}>
                🔎 {gateNotice}
              </div>
            )}
            <button
              type="button"
              className="primary"
              onClick={save}
              disabled={!dirty || busy}
            >
              Speichern
            </button>
            {timesheet.state === "pending_review" ? (
              <>
                {/* #506: „Prüfen"-Karte hat zwei menschliche Ausgänge — Freigabe
                    ODER Re-OCR (statt Validieren, das aus pending_review 409 gäbe). */}
                <button
                  type="button"
                  className="primary success"
                  onClick={approveReview}
                  disabled={busy}
                  title="Geprüft und in Ordnung → weiter zu OCR fertig (danach validierbar). Die Werte bleiben unverändert."
                >
                  ✓ Geprüft → freigeben
                </button>
                <button
                  type="button"
                  className="link"
                  onClick={reOcr}
                  disabled={busy}
                  title="Neu einlesen lassen (bestehende Korrekturen bleiben erhalten)."
                >
                  ↻ Neu lesen lassen
                </button>
              </>
            ) : (
              <button
                type="button"
                className="primary success"
                onClick={validate}
                disabled={busy || !pendingEmployeeId || plausCounts.red > 0 || (timesheet as any).missing_schedule === true}
                title={
                  (timesheet as any).missing_schedule === true
                    ? "Mitarbeiter hat keine Sollzeiten konfiguriert — bitte erst die Wochenarbeitszeit in den Mitarbeiter-Einstellungen hinterlegen"
                    : plausCounts.red > 0
                      ? `${plausCounts.red} Plausibilitäts-Fehler müssen zuerst korrigiert werden (rote Markierungen in der Tabelle)`
                      : !pendingEmployeeId
                        ? "Zuerst Mitarbeiter zuordnen"
                        : ""
                }
              >
                Validieren
              </button>
            )}
          </>
        )}
      </CardDetailActions>
    </DetailViewShell>
  );

  if (modal) {
    return (
      <DetailModal dirty={dirty} onClose={closePage}>
        {inner}
      </DetailModal>
    );
  }
  return inner;
}

/**
 * Mobile day-cards layout — replaces the 6-column table on screens <768px.
 * Each day = one card with stacked rows for von1/bis1/von2/bis2 + symbol.
 *
 * Anti-drift: confidence-bucket colouring, tier-borders, and feedback-on-blur
 * are computed identically to the desktop table — same input behaviour, just
 * a different container layout.
 */
function DayCardsList({
  days,
  dayData,
  cellConfidence,
  cellOriginal,
  suggestions,
  setField,
  lockedForEdit,
  postValidate,
  timesheetId,
}: {
  days: number;
  dayData: Record<number, DayData>;
  cellConfidence: Record<number, ConfidenceMap>;
  cellOriginal: Record<number, DayData>;
  suggestions: SuggestionMap;
  setField: (day: number, field: FieldName, value: string) => void;
  lockedForEdit: boolean;
  postValidate: boolean;
  timesheetId: string;
}) {
  return (
    <div className="day-cards">
      {Array.from({ length: days }, (_, i) => i + 1).map((day) => {
        const d = dayData[day] ?? {};
        const conf = cellConfidence[day] ?? {};
        const orig = cellOriginal[day] ?? {};
        return (
          <div key={day} className="day-card">
            <div className="day-card-head">Tag {day}</div>
            <div className="day-card-fields">
              {FIELDS.map((f) => {
                const bucket = bucketOf(conf[f] ?? null);
                const current = d[f] ?? "";
                const tierFromBucket = {
                  green: "tier-1",
                  yellow: "tier-2",
                  red: "tier-3",
                  none: "tier-4",
                }[bucket];
                const scope = `days.*.${f}`;
                const listId = `sugg-m-${f}`;
                const sugList = suggestions[scope] ?? [];
                const labelMap: Record<FieldName, string> = {
                  von1: "von 1",
                  bis1: "bis 1",
                  von2: "von 2",
                  bis2: "bis 2",
                  symb: "Symbol",
                };
                return (
                  <label
                    key={f}
                    className={`day-card-field conf-${bucket} ${tierFromBucket}`}
                  >
                    <span className="day-card-field-label">{labelMap[f]}</span>
                    <input
                      type="text"
                      className="cell-input"
                      value={current}
                      list={sugList.length > 0 ? listId : undefined}
                      onChange={(e) => setField(day, f, e.target.value)}
                      onBlur={() => { /* server-side corrections-Tracking nach Migration 034 */ }}
                      disabled={lockedForEdit}
                      placeholder={f === "symb" ? "U/K/ZA…" : "HH:MM"}
                      inputMode={f === "symb" ? "text" : "numeric"}
                    />
                    {sugList.length > 0 && (
                      <datalist id={listId}>
                        {sugList.map((s) => (
                          <option key={s.value} value={s.value} />
                        ))}
                      </datalist>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ─── Compare-View ────────────────────────────────────────────────────────────
//
// One row per day with 5 cell-blocks side-by-side: [PDF-crop : input] each
// for von1 | bis1 | von2 | bis2 | symb. PDF-crop is *exactly* what the OCR
// engine saw (220-DPI processed image, pad=4 around the bbox). User edits in
// place; same dayData state + setField setter as the Übersicht-View, so the
// existing dirty-tracking + save flow works unchanged.

function CompareView({
  days, timesheetId, dayData, cellConfidence, cellKlasse, cellOriginal,
  thresholds, setField, lockedForEdit, postValidate, activeCell, onCellHover,
}: {
  days: number;
  timesheetId: string;
  dayData: Record<number, DayData>;
  cellConfidence: Record<number, ConfidenceMap>;
  cellKlasse: Record<number, KlasseMap>;
  cellOriginal: Record<number, DayData>;
  thresholds: { red: number; green: number };
  setField: (day: number, field: FieldName, value: string) => void;
  lockedForEdit: boolean;
  postValidate: boolean;
  activeCell: { day: number; field: FieldName } | null;
  onCellHover: (c: { day: number; field: FieldName } | null) => void;
}) {
  // Available cell-image keys ("d01_von1"…). Karten bei denen das Grid-Detection
  // eine Spalte verfehlt hat (z.B. symb komplett fehlend) haben weniger als
  // 31×5 keys — wir rendern img-tags nur für vorhandene, sonst 404-Sturm in
  // Devtools/Logs (Anlassfall 2026-05-19, Karte 6d76180f hat 0 symb-cells).
  // Bei 404 (Karte mit altem OCR ohne bbox-Persistenz) → leerer Set, Frontend
  // skipt alle Bilder still.
  const [imageKeys, setImageKeys] = useState<ReadonlySet<string>>(new Set());
  // alignment: Server-computed Anchor-Truth-Check. Wenn false → Grid mis-
  // aligned → User-Eingaben würden auf falsche Tage geschrieben. HART blocken.
  // null = unbekannt (alte Karten ohne anchor-Persistenz) → erlauben aber.
  const [alignment, setAlignment] = useState<{
    aligned: boolean | null;
    delta_top_pct: number | null;
    delta_bot_pct: number | null;
  } | null>(null);
  const [keysLoaded, setKeysLoaded] = useState(false);
  useEffect(() => {
    setKeysLoaded(false);
    setAlignment(null);
    const ctrl = new AbortController();
    api.timesheets.cellImageKeys(timesheetId, ctrl.signal).then(
      (r) => {
        setImageKeys(new Set(r.keys));
        setAlignment(r.meta?.alignment ?? null);
        setKeysLoaded(true);
      },
      () => { setImageKeys(new Set()); setKeysLoaded(true); },
    );
    return () => ctrl.abort();
  }, [timesheetId]);
  // Click-to-zoom: single-zoom-pattern (2026-05-19). Nur EINE Zelle ist
  // gleichzeitig gezoomt — Klick auf andere Zelle togglet die alte zu und
  // öffnet die neue. Klick irgendwo außerhalb schließt den Zoom komplett.
  // Beim Zoom lädt das Image mit ?expand=2 (~2 Zell-Breiten Rand) damit
  // Nachbar-Zellen mitsichtbar sind — Use-case: User schreibt über den
  // Zell-Rand, ohne expand siehst du den überstrudelten Teil nicht.
  const [zoomedCell, setZoomedCell] = useState<{ day: number; field: FieldName } | null>(null);
  const toggleZoom = (day: number, field: FieldName) => {
    setZoomedCell((prev) => {
      if (prev && prev.day === day && prev.field === field) return null;
      return { day, field };
    });
  };
  // Globaler Klick-Listener: alles außerhalb eines zoomed image schließt den
  // Zoom. Wir checken über data-attr ob das clicked element selbst ein
  // zoomed-image ist (toggleZoom auf onClick übernimmt das ja schon, wir
  // wollen NICHT doppelt toggeln). data-zoom-img markiert das img-Element.
  useEffect(() => {
    if (!zoomedCell) return;
    const onDocClick = (e: globalThis.MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest("[data-zoom-img='true']")) return;  // image selbst — ihr onClick toggelt
      setZoomedCell(null);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [zoomedCell]);
  // HART-BLOCK bei Highlight-Drift (2026-05-20, Karte 29c5b5af Anlassfall):
  // wenn anchor-truth gegen das detected Grid driftet (>1.5%), zeigt der
  // Compare-View Tag-Crops vom FALSCHEN Tag (off by N rows). User-Eingaben
  // gehen auf data.days[N] aber das gezeigte Bild ist row N+/-X — silent
  // Daten-Korruption. Keine Edits erlauben, nur PDF visuell + Hinweis.
  if (alignment?.aligned === false) {
    const dt = ((alignment.delta_top_pct ?? 0) * 100).toFixed(1);
    const db = ((alignment.delta_bot_pct ?? 0) * 100).toFixed(1);
    const previewUrl = new URL(
      `api/cards/timesheet/${timesheetId}/preview-cached`,
      document.baseURI,
    ).toString();
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{
          background: "#fef2f2", border: "1px solid #dc2626", borderLeft: "4px solid #dc2626",
          padding: 14, borderRadius: 4, fontSize: 13, color: "#7f1d1d",
        }}>
          <strong>❌ Compare gesperrt — Highlight-Drift erkannt</strong>
          <div style={{ marginTop: 6 }}>
            Das detected Grid ist gegenüber den echten Tag-Labels im PDF
            verschoben (Δtop={dt}%, Δbot={db}%, Schwelle 1.5%). Bedeutet:
            wenn du im Compare-View „Tag 22" editierst, geht der Wert zwar
            in <code>data.days.22</code>, aber das Crop daneben zeigt
            tatsächlich einen anderen Tag aus dem PDF. <strong>Silent
            Daten-Korruption.</strong>
          </div>
          <div style={{ marginTop: 8 }}>
            Lösung: Karte neu OCR'n (Tier-3 4-way exhaustive findet meist
            korrekte Rotation/Grid) ODER manuell via Übersicht arbeiten,
            wo du das PDF direkt siehst und Werte einzeln pro Tag-Eingabe
            zuordnest.
          </div>
        </div>
        <img
          src={previewUrl}
          alt="PDF-Vorschau (Compare gesperrt)"
          style={{
            maxWidth: "100%", maxHeight: "85vh", objectFit: "contain",
            border: "1px solid #e5e7eb", borderRadius: 4, background: "#fff",
          }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
        />
      </div>
    );
  }

  // Compare-View ist nutzlos ohne cell_dispatch-Output (bboxes.json + processed PNG).
  // Bei pending_review-Karten ohne OCR-Pipeline-Lauf (manual_orientation oder
  // ocr_quality_below_threshold mit 0 cells) ist die Tabelle leer mit 150 Inputs
  // ohne Crops daneben — sieht broken aus. Banner mit Erklärung + Fallback:
  // PDF-Preview anzeigen damit der User wenigstens visuell prüfen kann was
  // auf der Karte steht (Diäten vs Stundenliste vs falsche Klassifizierung).
  if (keysLoaded && imageKeys.size === 0) {
    const previewUrl = new URL(
      `api/cards/timesheet/${timesheetId}/preview-cached`,
      document.baseURI,
    ).toString();
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{
          background: "#fffbeb", border: "1px solid #f59e0b", borderLeft: "4px solid #f59e0b",
          padding: 14, borderRadius: 4, fontSize: 13, color: "#92400e",
        }}>
          <strong>⚠ Per-Zellen-Vergleich nicht verfügbar</strong>
          <div style={{ marginTop: 6 }}>
            Diese Karte hat keine per-Zelle OCR-Crops (bboxes.json fehlt).
            Das passiert wenn die OCR-Pipeline für diese Karte nicht sauber
            durchgelaufen ist (z.B. <code>pending_review</code> oder vor
            cell_dispatch-Persistenz). Stattdessen unten das volle PDF-Bild
            zur visuellen Inspektion.
          </div>
        </div>
        <img
          src={previewUrl}
          alt="PDF-Vorschau"
          style={{
            maxWidth: "100%", maxHeight: "85vh", objectFit: "contain",
            border: "1px solid #e5e7eb", borderRadius: 4, background: "#fff",
          }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
        />
      </div>
    );
  }
  return (
    <table className="ts-compare-table">
      <thead>
        <tr>
          <th>Tag</th>
          <th>von1</th>
          <th>bis1</th>
          <th>von2</th>
          <th>bis2</th>
          <th>Symbol</th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: days }, (_, i) => i + 1).map((day) => {
          const d = dayData[day] ?? {};
          const conf = cellConfidence[day] ?? {};
          const orig = cellOriginal[day] ?? {};
          const klassen = cellKlasse[day] ?? {};
          return (
            <tr key={day}>
              <td className="ts-compare-day">{day}</td>
              {FIELDS.map((f) => {
                const v = (d[f] ?? "") as string;
                const c = conf[f];
                const k = klassen[f];
                const o = orig[f];
                const status = cellStatus(k, c, v, thresholds);
                const isActive = activeCell?.day === day && activeCell?.field === f;
                const isZoomed = zoomedCell?.day === day && zoomedCell?.field === f;
                // Zoom lädt mit expand=2 (Nachbar-Zellen im Bild) + highlight=1
                // (Server zeichnet Rahmen um die zentrale Zelle, damit der
                // Kontext erhalten bleibt — sonst verschwimmt sie mit den
                // ähnlich aussehenden Nachbarn). Normale Cells nutzen das
                // schmale Crop ohne Highlight — Cache-Hit der bestehenden
                // Bilder bleibt erhalten.
                const cellImageUrl = new URL(
                  `api/timesheets/${timesheetId}/cell-image?day=${day}&field=${f}${isZoomed ? "&expand=2&highlight=1" : ""}`,
                  document.baseURI,
                ).toString();
                // Plausibility check: value-based (08:03 atypical) + sequence
                // (von1<bis1<von2<bis2). Returns merged severity + tooltip reasons.
                // Post-Validate: pauschale Akzeptanz → kein Marker.
                const plausResult = cellPlausibility(f as any, v, d as any);
                const plaus = postValidate ? "ok" : plausResult.severity;
                const plausCls = plaus === "red" ? "ts-plaus-red"
                              : plaus === "yellow" ? "ts-plaus-yellow" : "";
                const plausTooltip = plausResult.reasons.join("\n");
                return (
                  <td key={f} className={`ts-compare-cell ${STATUS_CLASS[status]} ${plausCls} ${isActive ? "ts-cell-active" : ""}`}
                      data-day={day}
                      data-field={f}
                      onMouseEnter={() => onCellHover({ day, field: f })}
                      onMouseLeave={() => onCellHover(null)}>
                    <div className="ts-compare-cell-inner">
                      {imageKeys.has(`d${String(day).padStart(2, "0")}_${f}`) && (
                        <img className={`ts-compare-img ${isZoomed ? "ts-compare-img-zoomed" : ""}`}
                             src={cellImageUrl} alt={`d${day} ${f}`}
                             loading="lazy"
                             data-zoom-img="true"
                             onClick={(e) => { e.stopPropagation(); toggleZoom(day, f); }}
                             title={isZoomed ? "klick = normal" : "klick = vergrößern (mit Nachbar-Zellen)"}
                             onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                      )}
                      <div className="ts-compare-input-wrap">
                        <input
                          type="text"
                          value={v}
                          placeholder={f === "symb" ? "—" : "HH:MM"}
                          onChange={(e) => {
                            // Raw input during typing — format on blur.
                            // Symb gets instant uppercase (p→P, za→ZA).
                            const next = f === "symb" ? e.target.value.toUpperCase() : e.target.value;
                            setField(day, f, next);
                          }}
                          onFocus={(e) => {
                            // Click overwrites the value directly: select all,
                            // then next keystroke replaces. No need to clear first.
                            e.currentTarget.select();
                            onCellHover({ day, field: f });
                          }}
                          onBlur={() => {
                            // Auto-format on blur (08:30 ← 830, 08:00 ← 0800/8).
                            // Symb-field nutzt classify() für Alias-Resolution
                            // (Pflegeurlaub → P) — Symmetrie zur Übersicht.
                            let formatted = v;
                            if (isTimeField(f)) {
                              formatted = formatTimeInput(v);
                            } else if (f === "symb") {
                              const r = classify(v, "symb");
                              formatted = r.normalized ?? v.trim().toUpperCase();
                            }
                            if (formatted !== v) setField(day, f, formatted);
                            // Correction-Tracking läuft seit Migration 034
                            // server-side im PATCH-Handler (siehe Übersicht-Hinweis).
                          }}
                          disabled={lockedForEdit}
                          className="ts-compare-input"
                        />
                        {c !== undefined && (
                          <span className="ts-compare-conf"
                                title={`Konfidenz ${(c * 100).toFixed(0)}%`}>
                            {(c * 100).toFixed(0)}%
                          </span>
                        )}
                        {plaus !== "ok" && (
                          <span className={`ts-plaus-badge ts-plaus-badge-${plaus} ts-tooltip-trigger`}
                                aria-label={plausTooltip}>
                            {plaus === "red" ? "!" : "?"}
                            <span className="ts-tooltip" role="tooltip">{plausTooltip}</span>
                          </span>
                        )}
                      </div>
                      {o !== undefined && v !== o && (
                        <span className="ts-compare-orig" title="Original (OCR)">
                          OCR: {o || "leer"}
                        </span>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
